/**
 * Interval between presence heartbeats for a mounted tab. Kept well under the
 * server's 60-second lease TTL (`PRESENCE_LEASE_TTL_SECONDS`) so one missed
 * or slow beat never lets a live tab's lease lapse.
 */
export const APPOINTMENT_PRESENCE_HEARTBEAT_INTERVAL_MS = 15_000;

interface AppointmentPresenceHeartbeatOptions {
  intervalMs?: number;
  schedule?: (run: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
  onError?: (error: unknown) => void;
}

/**
 * Framework-free heartbeat loop for one tab's presence lease. Beats
 * immediately on start, then on an interval for as long as it runs — hidden
 * or visible (NO-428: an open hidden tab still counts as the RD being on the
 * Appointment, so only unmounting stops the loop and lets the lease lapse).
 * Beats never overlap: the next one is scheduled only after the previous
 * settles. Failures are deduped per consecutive run: only the FIRST failed
 * beat after a success (or since construction) reaches `onError`, a
 * successful beat re-arms it, and the loop keeps going either way — presence
 * is best-effort and self-heals on the next interval, so per-beat reporting
 * would only turn an outage into telemetry spam.
 */
export class AppointmentPresenceHeartbeat {
  private disposed = false;
  private inFlight = false;
  private failureReported = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly intervalMs: number;
  private readonly schedule: NonNullable<
    AppointmentPresenceHeartbeatOptions["schedule"]
  >;
  private readonly cancel: NonNullable<AppointmentPresenceHeartbeatOptions["cancel"]>;
  private readonly onError: (error: unknown) => void;

  constructor(
    private readonly beat: () => Promise<void>,
    options: AppointmentPresenceHeartbeatOptions = {}
  ) {
    this.intervalMs = options.intervalMs ?? APPOINTMENT_PRESENCE_HEARTBEAT_INTERVAL_MS;
    const schedule = options.schedule;
    const cancel = options.cancel;
    this.schedule = schedule
      ? (run, delay) => schedule(run, delay)
      : (run, delay) => globalThis.setTimeout(run, delay);
    this.cancel = cancel
      ? (timer) => cancel(timer)
      : (timer) => globalThis.clearTimeout(timer);
    this.onError = options.onError ?? (() => {});
  }

  start(): void {
    this.beatNow();
  }

  dispose(): void {
    if (this.disposed) return;

    this.disposed = true;
    this.clearTimer();
  }

  private beatNow(): void {
    if (this.disposed || this.inFlight) return;

    this.clearTimer();
    this.inFlight = true;
    void this.beat()
      .then(() => {
        this.failureReported = false;
      })
      .catch((error: unknown) => {
        if (this.disposed || this.failureReported) return;

        this.failureReported = true;
        this.onError(error);
      })
      .finally(() => {
        this.inFlight = false;
        this.scheduleNext();
      });
  }

  private scheduleNext(): void {
    if (this.disposed || this.timer) return;

    this.timer = this.schedule(() => {
      this.timer = null;
      this.beatNow();
    }, this.intervalMs);
  }

  private clearTimer(): void {
    if (!this.timer) return;

    this.cancel(this.timer);
    this.timer = null;
  }
}
